import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, FileSpreadsheet, Printer } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

export const VatTab = ({ loading, vatData, periodType, setPeriodType, onRefresh, exportToCsv, handlePrint }) => {
  return (
    <div>
      {/* Period Filter */}
      <div className="mb-4 flex items-center gap-4">
        <Select value={periodType} onValueChange={setPeriodType}>
          <SelectTrigger className="w-[150px]" data-testid="period-selector">
            <SelectValue placeholder="Period Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="monthly">Monthly</SelectItem>
            <SelectItem value="quarterly">Quarterly</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={onRefresh} data-testid="apply-vat-period">
          Apply Period
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading VAT statement...</div>
      ) : vatData ? (
        <div className="space-y-6">
          {/* VAT Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-l-4 border-l-[hsl(188,64%,38%)]" data-testid="output-vat-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Output VAT (Sales)</CardTitle>
                <CardDescription>VAT collected from customers</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-semibold font-mono text-[hsl(188,64%,38%)]">
                  {formatCurrency(vatData.output_vat.vat_amount)}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  On {formatCurrency(vatData.output_vat.taxable_amount)} taxable
                </p>
                <p className="text-xs text-muted-foreground">
                  {vatData.output_vat.invoice_count} invoices
                </p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-[hsl(4,79%,55%)]" data-testid="input-vat-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Input VAT (Purchases)</CardTitle>
                <CardDescription>VAT paid to suppliers</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-semibold font-mono text-[hsl(4,79%,55%)]">
                  {formatCurrency(vatData.input_vat.vat_amount)}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  On {formatCurrency(vatData.input_vat.taxable_amount)} purchases
                </p>
                <p className="text-xs text-muted-foreground">
                  {vatData.input_vat.expense_count} expenses
                </p>
              </CardContent>
            </Card>

            <Card className={`border-l-4 ${vatData.net_vat.amount >= 0 ? 'border-l-amber-500' : 'border-l-emerald-500'}`} data-testid="net-vat-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Net VAT</CardTitle>
                <CardDescription>{vatData.net_vat.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className={`text-3xl font-semibold font-mono ${vatData.net_vat.amount >= 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                  {formatCurrency(Math.abs(vatData.net_vat.amount))}
                </p>
                <Badge 
                  variant={vatData.net_vat.status === 'Payable' ? 'secondary' : 'outline'}
                  className={`mt-2 ${vatData.net_vat.status === 'Payable' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}
                >
                  {vatData.net_vat.status}
                </Badge>
              </CardContent>
            </Card>
          </div>

          {/* VAT Breakdown Chart */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>VAT Breakdown by Month</CardTitle>
                <CardDescription>
                  Period: {vatData.period.start} to {vatData.period.end}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => exportToCsv(vatData.monthly_breakdown, 'vat_statement')}>
                  <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrint}>
                  <Printer className="w-4 h-4 mr-1" /> Print
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={vatData.monthly_breakdown}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="output_vat" name="Output VAT" fill="hsl(188, 64%, 38%)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="input_vat" name="Input VAT" fill="hsl(4, 79%, 55%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Detailed Tables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Monthly VAT Details</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month</TableHead>
                      <TableHead className="text-right">Output</TableHead>
                      <TableHead className="text-right">Input</TableHead>
                      <TableHead className="text-right">Net</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vatData.monthly_breakdown.map((m) => (
                      <TableRow key={m.month}>
                        <TableCell className="font-medium">{m.month}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(m.output_vat)}</TableCell>
                        <TableCell className="text-right font-mono text-red-500">{formatCurrency(m.input_vat)}</TableCell>
                        <TableCell className={`text-right font-mono font-semibold ${m.net_vat >= 0 ? '' : 'text-emerald-600'}`}>
                          {formatCurrency(m.net_vat)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Input VAT by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Taxable</TableHead>
                      <TableHead className="text-right">VAT</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vatData.input_vat.by_category?.map((c) => (
                      <TableRow key={c.category}>
                        <TableCell className="font-medium">{c.category}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(c.taxable)}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(c.vat)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* UAE VAT Filing Info */}
          <Card className="bg-blue-50/50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-blue-100">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-blue-900">UAE VAT Filing Information</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    VAT returns are filed {periodType === 'quarterly' ? 'quarterly' : 'monthly'} via the FTA e-Services portal. 
                    The standard UAE VAT rate is 5%. Filing deadline is typically 28 days after the tax period ends.
                  </p>
                  <p className="text-xs text-blue-600 mt-2">
                    Note: This report is for reference only. Please verify all figures before official submission.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  );
};
